import { TriggerCreate } from "@dots-projects/types";
import { v4 as uuidv4 } from "uuid";
import gif from "./private-onboarding-gif.gif";

export const privateOnboardingTemplate = (
  workspace: string
): {
  trigger: TriggerCreate;
  gif: any;
  title: string;
  description: string;
} => {
  return {
    title: "Private Channel Onboarding",
    description: `<p>Welcome new members to your server in a personalized way! With this template, Dots will automatically create a custom onboarding channel to introduce the member to your server. Once the onboarding is completed, the channel will be deleted.</p><p className="mt-10">Recommended for small to medium sized communities. Explore the logic of this template in the editor view on the left.</p>`,
    gif,
    trigger: {
      name: "Private Channel Onboarding",
      type: "team_join",
      platform: "discord",
      communityId: localStorage.getItem("guild-id") || "",
      authorUserId: localStorage.getItem("discord-id") || "",
      isDraft: true,
      workspace,
      steps: [
        {
          id: "send_message-0-g8Wbh2",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Thanks! Welcome to the community.",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage: "<p>Thanks! Welcome to the community.</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [],
        },
        {
          id: "send_message-2-Wc2HvL",
          name: "Not an email",
          type: "send_message",
          configs: {
            message:
              "This doesn't look like a valid email address. Please try again",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            removeUrlEmbeds: false,
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "idle-1-TA5eWZ",
            },
          ],
        },
        {
          id: "idle-1-0us0or",
          name: "Wait for captcha response",
          type: "idle",
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "question-msg-send_message-0-8qiCL4",
              when: {
                userResponse: {
                  type: "captcha-result",
                  userId: "{{user.id}}",
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                  succeeded: true,
                },
              },
            },
            {
              targetStepId: "test_captcha-0-vsHX0p",
              when: {
                userResponse: {
                  type: "captcha-result",
                  userId: "{{user.id}}",
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                  succeeded: false,
                },
              },
            },
          ],
        },
        {
          id: "create_channel-0--Snx2D",
          name: "Welcome in a private channel",
          type: "create_channel",
          configs: {
            userIds: ["{{user.id}}"],
            isPrivate: true,
            channelName: "onboard-{{user.username}}",
            channelType: "onboarding",
          },
          isEntry: true,
          isGoal: false,
          transitions: [
            {
              targetStepId: "test_captcha-0-vsHX0p",
            },
          ],
        },
        {
          id: "test_captcha-0-vsHX0p",
          name: "Send a captcha",
          type: "test_captcha",
          configs: {
            userId: "{{user.id}}",
            message:
              "Hello <@{{user.id}}> you aren't a robot right? Please solve this",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            failMessage:
              "Sorry <@{{user.id}}> sorry you failed all attempts. Please request a new captcha!",
            $$failEditorHtmlMessage:
              '<p>Sorry <span class="mention" data-index="0" data-id="<@user.id>" data-denotation-char="{{" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> sorry you failed all attempts. Please request a new captcha!</p',
            $$succeedEditorHtmlMessage:
              '<p>Hello <span class="mention" data-index="3" data-denotation-char="{{" data-id="<@user.id>" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> you aren\'t a robot right? Please solve this.</p>',
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "idle-1-0us0or",
            },
          ],
        },
        {
          id: "question-msg-send_message-0-8qiCL4",
          name: "Ask first name",
          type: "send_message",
          configs: {
            message: "What is your first name?",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage: "<p>What is your first name?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_survey-0-pP-XKR",
              when: {
                userResponse: {
                  type: "message",
                  userId: "{{user.id}}",
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                },
              },
            },
          ],
        },
        {
          id: "send_survey-0-pP-XKR",
          name: "Send a survey (single selection)",
          type: "send_survey",
          configs: {
            type: "buttons",
            message:
              "Hi {{question-msg-send_message-0-8qiCL4.userResponse.content}} ! What brings you here?",
            options: [
              {
                id: "na",
                emoji: "🤝",
                label: "Meet others",
              },
              {
                id: "796bc15d-2932-473d-8547-9f47be1f8854",
                emoji: "👀",
                label: "See what others are building",
              },
              {
                id: "b7c52888-a0a2-4eff-b5f7-ca51d2e69201",
                emoji: "🎓",
                label: "Learn",
              },
            ],
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            $$editorHtmlMessage:
              '<p>Hi <span class="mention" data-index="1" data-denotation-char="{{" data-id="question-msg-send_message-0-8qiCL4.userResponse.content" data-value="Ask first name.userResponse.content}}">﻿<span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>Ask first name.userResponse.content}}</span>﻿</span> ! What brings you here?</p>',
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-S-qANM",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                },
              },
            },
            {
              targetStepId: "send_message-0-qxpU4U",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "796bc15d-2932-473d-8547-9f47be1f8854",
                },
              },
            },
            {
              targetStepId: "send_message-0-8Ajyxy",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "b7c52888-a0a2-4eff-b5f7-ca51d2e69201",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-S-qANM",
          name: "Send a message",
          type: "send_message",
          configs: {
            message:
              "Amazing! Check out our channel xyz, and introduce yourself!",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>Amazing! Check out our channel xyz, and introduce yourself! </p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-dUbMbS",
            },
          ],
        },
        {
          id: "send_message-0-qxpU4U",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Check out demos to see what others are building!",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>Check out demos to see what others are building!</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-dUbMbS",
            },
          ],
        },
        {
          id: "send_message-0-8Ajyxy",
          name: "Send a message",
          type: "send_message",
          configs: {
            message:
              "Our help portal has a variety of articles on how to learn. Please check it out here ->",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>Our help portal has a variety of articles on how to learn. Please check it out here -&gt;</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-dUbMbS",
            },
          ],
        },
        {
          id: "send_message-0-dUbMbS",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Hello! What's your email address? Type skip to skip!",
            channelId: "{{create_channel-0--Snx2D.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>Hello! What's your email address? Type skip to skip!</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "idle-1-TA5eWZ",
            },
          ],
        },
        {
          id: "idle-1-TA5eWZ",
          name: "Wait for email",
          type: "idle",
          configs: {
            reference: "send_message-0-dUbMbS",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-2-Wc2HvL",
              when: {
                userResponse: {
                  and: [
                    {
                      content: {
                        hasnot: "email",
                      },
                    },
                    {
                      content: {
                        neq: "skip",
                      },
                    },
                  ],
                  type: "message",
                  userId: "{{user.id}}",
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                },
              },
            },
            {
              targetStepId: "send_message-0-g8Wbh2",
              when: {
                userResponse: {
                  type: "message",
                  userId: "{{user.id}}",
                  content: {
                    has: "email",
                  },
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                },
              },
            },
            {
              targetStepId: "send_message-0-g8Wbh2",
              when: {
                userResponse: {
                  type: "message",
                  userId: "{{user.id}}",
                  content: "skip",
                  channelId: "{{create_channel-0--Snx2D.channelId}}",
                },
              },
            },
          ],
        },
      ],
    },
  };
};
